import { SupportedChainId } from 'config/chains'
import { useConnection } from 'hooks/useConnection'
import { ReactNode, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { ApplicationModal } from 'state/application/reducer'
import { isDevelopmentEnv, isStagingEnv } from 'utils/env'

interface SchemaItemBase {
  id: string
  label: ReactNode
  href?: string // Add href to base interface
  external?: boolean
}

interface SchemaLink {
  id: string
  href: string
  label: string
  external?: boolean
}

export interface SchemaItemSingle extends SchemaItemBase {
  to: string
  sublinks?: never
  name?: never
}

export interface SchemaItemMultiple extends SchemaItemBase {
  to?: string
  sublinks?: SchemaLink[]
  name: ApplicationModal
}

export type SchemaTypeIsActiveExtend = { isActive: boolean }
export type SchemaTypeDefaultExtend = object

export type SchemaType<T extends SchemaTypeDefaultExtend = SchemaTypeDefaultExtend> = ((
  | SchemaItemMultiple
  | SchemaItemSingle
) &
  T)[]

const XKDK_MIGRATOR_START_TIME = 1766498400_000 // Dec 23
const XKDK_MIGRATOR_END_TIME = XKDK_MIGRATOR_START_TIME + 30 * 24 * 60 * 60 * 1000
const now = Date.now()

export const Schema: SchemaType = [
  {
    id: 'swap-nav-link',
    label: <>Trade</>,
    // to: '/swap',
    href: '/swap',
    sublinks: [
      {
        id: 'swap-nav-link',
        href: '/swap',
        label: 'Swap',
        external: false,
      },
      // {
      //   id: `b-options`,
      //   href: 'https://options.kodiak.finance',
      //   label: 'bOptions',
      //   external: true,
      // },
      {
        id: 'perps',
        href: 'https://perps.kodiak.finance/perp/PERP_ETH_USDC',
        label: 'Perps',
        external: true,
      },
    ],
    name: ApplicationModal.TRADE,
  },
  {
    id: `liquidity-nav-link`,
    to: '/liquidity/pools',
    label: <>Liquidity</>,
  },
  {
    id: 'explore-nav-link',
    to: '/explore',
    label: <>Explore</>,
  },
  ...(now >= XKDK_MIGRATOR_START_TIME
    ? [
        {
          id: 'staking-nav-link',
          href: '/staking',
          label: <>Staking</>,
          name: ApplicationModal.CLAIM_POPUP,
          sublinks:
            now <= XKDK_MIGRATOR_END_TIME
              ? [
                  {
                    id: 'staking-nav-sublink',
                    href: '/staking',
                    label: 'Staking',
                    external: false,
                  },
                  {
                    id: 'claim-nav-sublink',
                    href: '/claim',
                    label: 'Claim',
                    external: false,
                  },
                ]
              : undefined,
        },
      ]
    : []),
  {
    id: 'bridge-nav-link',
    to: 'https://bridge.berachain.com',
    label: <>Bridge</>,
    external: true,
  },
]

const BERA_ONLY_NAV_IDS = ['staking-nav-link']

const BRIDGE_URLS: Partial<Record<SupportedChainId, string>> = {
  [SupportedChainId.ROBINHOOD]: 'https://relay.link/bridge/robinhood',
}

const isExternalLink = (href: string): boolean => {
  return href.startsWith('http') || href.startsWith('https')
}

const applyChainOverrides = (schema: SchemaType, chainId: SupportedChainId): SchemaType =>
  schema
    .filter((item) => chainId === SupportedChainId.BERA || !BERA_ONLY_NAV_IDS.includes(item.id))
    .map((item) => {
      if (item.id === 'bridge-nav-link' && BRIDGE_URLS[chainId]) {
        return { ...item, to: BRIDGE_URLS[chainId] } as SchemaItemSingle
      }
      return item
    })

export const useSchema = (): SchemaType<SchemaTypeIsActiveExtend> => {
  const { pathname } = useLocation()
  const { chainId } = useConnection()

  const isLiquidityLink = pathname.startsWith('/liquidity')

  return useMemo(
    () =>
      applyChainOverrides(Schema, chainId).map((item) => {
        // Filter out Bault Analytics in production
        if (item.id === 'analytics-nav-link' && item.sublinks) {
          const filteredSublinks = item.sublinks.filter(
            (sublink) => sublink.id !== 'bault-analytics-nav-link' || isDevelopmentEnv() || isStagingEnv()
          )
          item = { ...item, sublinks: filteredSublinks }
        }
        let isActive = false

        // Don't mark as active if the item or all its sublinks are external
        if (item.external) {
          isActive = false
        } else if (item.to) {
          // Single link items
          isActive = pathname.startsWith(item.to)
        } else if (item.href && !isExternalLink(item.href)) {
          // Items with href that aren't external
          isActive = pathname.startsWith(item.href)
        } else if (item.sublinks) {
          // Items with sublinks - only check internal links
          isActive = item.sublinks.some((link) => !link.external && pathname.startsWith(link.href))
        }

        return {
          ...item,
          isActive,
        }
      }),
    [pathname, chainId]
  )
}

export default Schema
